import {naturalSortByFunction, partition} from '@benchling/js-utils';
import React, {ReactNode} from 'react';
import _ from 'underscore';

import {DataTableColumn} from 'notebook/data-tables/DataTableConstants';
import {Review} from 'notebook/reviews/ReviewCriteriaConstants';
import {SimpleDownloadAsCSVButton} from 'notebook/tables/export/DownloadAsCSVButton';
import {PROXIED_NOTE_PROP_KEYS, ProxiedNoteProps} from 'notebook/util/NoteUtilConstants';
import {AssaySchema} from 'results/assay-schemas/AssaySchemaConstants';
import {AssayResult} from 'results/assays/AssayRecordConstants';
import {CellWrapper, renderFieldForSpreadsheet} from 'results/assays/AssayRecordRendering';
import {getPrimaryLinkField} from 'results/PrimaryLinks';
import {DataMissing} from 'results/reduceResults';
import {DisplayConfig, DisplayConfigWithoutReviewed} from 'runs/AttachmentConstants';
import {
  ADDITIONAL_METADATA,
  DEFAULT_MIN_COLUMN_WIDTH,
  fieldToColumnWidth,
  getColumnLabelForField,
  getSpecialColumns,
  PRIMARY_METADATA,
} from 'runs/Columns';
import DisplayConfigDropdown from 'runs/DisplayConfigDropdown';
import FixedColumnAssayResultTable from 'runs/FixedColumnAssayResultTable';
import {shouldDisablePrimaryLinkColumn} from 'runs/shouldDisablePrimaryLinkColumn';
import withPublishedDataTableColumnsQuery from 'runs/withPublishedDataTableColumnsQuery';
import {SchemaField} from 'schemas/fields/SchemaFieldConstants';
import {filterToSchemaTags} from 'schemas/tags/TagUtil';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import {MakeFieldsRequired, Optional} from 'util/Types';
import {SAMPLE_COLUMN_LABEL} from 'workflows/WorkflowConstants';

import {FixedColumn, SpecialColumn} from './ColumnConstants';

const PRIMARY_COLUMN_WIDTH = 150;

type ProcedureAssayResult = MakeFieldsRequired<AssayResult, 'conditionAndReplicate'>;

/**
 * Returns display config without `hideReviewed`.
 *
 * This component no longer includes a column for review status so we want to exclude the `hideReviewed`
 * param from the display config that is being used and updated.
 */
function adaptDisplayConfigToExcludeReviewed(displayConfig: DisplayConfig): DisplayConfigWithoutReviewed {
  const configWithoutReviewed = {...displayConfig};
  delete configWithoutReviewed.hideReviewed;
  return configWithoutReviewed;
}

/**
 * Returns a list of schema names to be hidden from the table
 *
 * For old results tables, any field not included in the published data table columns should be hidden.
 * For new results table, all fields are visible since the new results table did away with the concept of
 * data table configs.
 */
function getHiddenColumnsFromSchema(
  schema: AssaySchema,
  columns: Optional<Array<DataTableColumn>>
): Array<string> {
  return !shouldDisablePrimaryLinkColumn() && columns
    ? schema.fields
        .filter((field) => !columns.some((column) => column.tagSchemaFieldId === field.id))
        .map((field) => field.name)
    : [];
}

export interface AssayResultsTableProps extends ProxiedNoteProps {
  additionalColumns: Array<FixedColumn>;
  attachedForReview?: boolean;
  autoCollapseLimit?: number;
  results: Array<AssayResult>;
  schema: AssaySchema;

  headerControls?: Array<ReactNode>;
  canRename: boolean;
  displayConfig?: DisplayConfig;
  downloadAsCSVButtonDataTestAttrs?: DataTestAttrs;
  forceRowCount?: number;
  isOversized?: boolean;
  onDownloadAsCSV?: () => void;
  review?: Review;
  updateDisplayConfigHandler: (schemaId: string, displayConfig: DisplayConfigWithoutReviewed) => void;
}

class AssayResultsTable extends React.Component<
  AssayResultsTableProps & {
    publishedDataTableColumnsBySchema: {[schemaId: string]: Optional<Array<DataTableColumn>>};
  }
> {
  static defaultProps = {
    attachedForReview: false,
    selectTable: () => {},
    additionalColumns: [],
  };

  _shouldRenderConditionAndReplicateColumns(): boolean {
    return this.props.results.some((result) => result.conditionAndReplicate != null);
  }

  _getDisplayConfig(): DisplayConfigWithoutReviewed {
    if (this.props.displayConfig) {
      return adaptDisplayConfigToExcludeReviewed(this.props.displayConfig);
    }

    return {
      hideId: true,
      hideCreator: false,
      hideCreatedAt: false,
      hiddenFields: getHiddenColumnsFromSchema(
        this.props.schema,
        this.props.publishedDataTableColumnsBySchema[this.props.schema.id]
      ),
    };
  }

  _getSpecialColumns(metadataType: string): Array<FixedColumn> {
    return getSpecialColumns()
      .filter(({metadataType: thisType}) => thisType === metadataType)
      .map((column: SpecialColumn) => ({
        hidden: this._getDisplayConfig()[column.isHiddenKey],
        label: column.name,
        width: column.width,
        render: (result: AssayResult) => ({
          formattedValue: <CellWrapper>{column.renderResult(result)}</CellWrapper>,
          text: column.resultToText(result),
          align: 'left',
        }),
        shouldAffix: metadataType === PRIMARY_METADATA,
      }));
  }

  _getColumnFromField(field: SchemaField, isPrimary: boolean): FixedColumn {
    return {
      hidden: !isPrimary && this._getDisplayConfig().hiddenFields.includes(field.name),
      shouldAffix: isPrimary,
      label: isPrimary ? SAMPLE_COLUMN_LABEL : getColumnLabelForField(field),
      width: isPrimary ? PRIMARY_COLUMN_WIDTH : this._getNonPrimaryColumnWidth(field),
      render: (result: AssayResult) => renderFieldForSpreadsheet(result, field),
      schemaField: field.apiId ? _.indexBy(this.props.schema.fields, 'apiId')[field.apiId] : null,
    };
  }

  _getNonPrimaryColumnWidth(field: SchemaField): number {
    const columns = this.props.publishedDataTableColumnsBySchema[this.props.schema.id];
    const column = columns ? columns.find((col) => col.tagSchemaFieldId === field.id) : null;
    return column ? column.width : fieldToColumnWidth(field);
  }

  _getPrimaryColumns(): Array<FixedColumn> {
    if (shouldDisablePrimaryLinkColumn()) {
      return [];
    }
    const primaryLinkField = getPrimaryLinkField(this.props.schema);
    return primaryLinkField ? [this._getColumnFromField(primaryLinkField, true)] : [];
  }

  _getFieldColumns(): Array<FixedColumn> {
    let fields = this.props.schema.fields;
    if (!shouldDisablePrimaryLinkColumn()) {
      const primaryLinkField = getPrimaryLinkField(this.props.schema);
      fields = fields.filter((field) => field !== primaryLinkField);
    }
    return fields.map((field) => this._getColumnFromField(field, false));
  }

  _getConditionAndReplicateColumns(): Array<FixedColumn> {
    return [
      {
        hidden: !this._shouldRenderConditionAndReplicateColumns(),
        shouldAffix: false,
        label: 'Condition',
        width: DEFAULT_MIN_COLUMN_WIDTH,
        render: (result: AssayResult) => ({
          text: result.conditionAndReplicate?.conditionName ?? '',
          align: 'left',
        }),
      },
      {
        hidden: !this._shouldRenderConditionAndReplicateColumns(),
        shouldAffix: false,
        label: 'Replicate',
        width: DEFAULT_MIN_COLUMN_WIDTH,
        render: (result: AssayResult) => ({
          text: result.conditionAndReplicate?.replicateNumber.toString() ?? '',
          align: 'left',
        }),
      },
    ];
  }

  _getPlaceholderColumn(): FixedColumn {
    // Used when no columns are selected
    return {
      label: 'No columns selected',
      width: 200,
      render: () => ({text: '', align: 'center'}),
    };
  }

  _compareProcedureResult = (r1: ProcedureAssayResult, r2: ProcedureAssayResult): number => {
    const conditionComparison = r1.conditionAndReplicate.conditionName.localeCompare(
      r2.conditionAndReplicate.conditionName
    );
    if (conditionComparison !== 0) {
      return conditionComparison;
    }
    return r1.conditionAndReplicate.replicateNumber - r2.conditionAndReplicate.replicateNumber;
  };

  _getSortedResults(): Array<AssayResult | DataMissing> {
    if (this._shouldRenderConditionAndReplicateColumns()) {
      const [nonProcedureResults, procedureResults] = partition(
        this.props.results,
        (r) => r.conditionAndReplicate == null
      );
      return [...procedureResults.sort(this._compareProcedureResult), ...nonProcedureResults];
    }

    if (shouldDisablePrimaryLinkColumn()) {
      return this.props.results;
    }

    // Until we support custom sorts on tables, we auto-sort by the primary-link field of the assay schema.
    const primaryLinkField = getPrimaryLinkField(this.props.schema);
    if (!primaryLinkField) {
      return this.props.results;
    }

    // Using "naturalSort" ensures that numerics are sorted properly. Without it:
    //   Value1, Value10, Value11, Value2, Value3...
    // With naturalSort:
    //   Value1, Value2, Value3...
    return naturalSortByFunction(this.props.results, (result) => {
      const sampleTag = filterToSchemaTags(result.tags).find(
        (tag) => tag.tagSchemaField.id === primaryLinkField.id
      );
      return sampleTag?.value ?? '';
    });
  }

  _getMetadataColumns(metadataType: string): Array<FixedColumn> {
    return [
      ...this._getSpecialColumns(metadataType),
      ...this.props.additionalColumns
        .filter(({metadataType: thisType}) => thisType === metadataType)
        .map((column) => ({hidden: false, ...column})),
    ];
  }

  override render(): JSX.Element {
    const shownColumns = [
      ...this._getPrimaryColumns(),
      ...this._getMetadataColumns(PRIMARY_METADATA),
      ...this._getFieldColumns(),
      ...this._getMetadataColumns(ADDITIONAL_METADATA),
      ...this._getConditionAndReplicateColumns(),
    ].filter((column: FixedColumn) => !column.hidden);
    if (shownColumns.length === 0) {
      shownColumns.push(this._getPlaceholderColumn());
    }

    const downloadAsCSVButton = this.props.onDownloadAsCSV && !this.props.viewer && (
      <SimpleDownloadAsCSVButton
        dataTestAttrs={this.props.downloadAsCSVButtonDataTestAttrs}
        onDownloadAsCSV={this.props.onDownloadAsCSV}
        key="download-as-csv-button"
      />
    );

    const headerControls =
      this.props.headerControls != null
        ? [downloadAsCSVButton, ...this.props.headerControls]
        : downloadAsCSVButton;
    return (
      <div>
        <FixedColumnAssayResultTable
          autoCollapseLimit={this.props.autoCollapseLimit}
          columns={shownColumns}
          results={this._getSortedResults()}
          schema={this.props.schema}
          tableName={this.props.schema.name}
          headerControls={headerControls}
          isOversized={this.props.isOversized}
          forceRowCount={this.props.forceRowCount}
          canRename={this.props.canRename}
          leftHeaderControls={
            !this.props.locked &&
            !this.props.viewer && (
              <DisplayConfigDropdown
                displayConfig={this._getDisplayConfig()}
                schema={this.props.schema}
                updateDisplayConfigHandler={this.props.updateDisplayConfigHandler}
              />
            )
          }
          dataTestAttrs={{component: 'AssayResultsTable', element: 'root'}}
          // eslint-disable-next-line no-restricted-syntax
          {..._.pick(this.props, PROXIED_NOTE_PROP_KEYS)}
        />
      </div>
    );
  }
}

export default withPublishedDataTableColumnsQuery(AssayResultsTable, (props) => [props.schema]);
